import React, { InputHTMLAttributes } from 'react'
import styled from 'styled-components'
import { colors } from '../styleGuide'

export interface DcCheckboxProps extends InputHTMLAttributes<HTMLInputElement> {
  type: 'checkbox'
  value: string
  disabled: boolean
  checked: boolean
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  name: string
  label: string
  checkboxSize?: 'sm' | 'lg'
}

interface StyledProps {
  checkboxSize?: 'sm' | 'lg'
}

const StyledDcCheckbox = styled.div<StyledProps>`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  gap: 0.5rem;
`

const StyledCheckBoxLabel = styled.label<StyledProps>`
  font-size: ${({ checkboxSize }) => {
    if (checkboxSize === 'sm') return '0.875rem' // 14px
    if (checkboxSize === 'lg') return '1.125rem' // 18px
    return '1rem' // 16px (default)
  }};

  line-height: 1.5;
  margin: 0;
  cursor: pointer;
  user-select: none;
`

const StyledCheckBox = styled.input<StyledProps>`
  border-radius: 0.25em;
  -webkit-appearance: none;
  appearance: none;
  background-color: ${colors.white};
  background-position: 50%;
  background-repeat: no-repeat;
  background-size: contain;
  border: 2px solid ${colors.grayBorder};
  height: ${(props) => {
    if (props.checkboxSize === 'sm') return '1rem'
    if (props.checkboxSize === 'lg') return '1.5rem'
    return '1.25rem' // default
  }};
  width: ${(props) => {
    if (props.checkboxSize === 'sm') return '1rem'
    if (props.checkboxSize === 'lg') return '1.5rem'
    return '1.25rem' // default
  }};
  margin: 0;
  cursor: pointer;
  flex-shrink: 0;

  :focus {
    border-color: ${colors.primary};
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
    outline: none;
  }

  :checked {
    background-color: ${colors.primary};
    border-color: ${colors.primary};
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'%3e%3cpath fill='none' stroke='%23fff' stroke-linecap='round' stroke-linejoin='round' stroke-width='3' d='m6 10 3 3 6-6'/%3e%3c/svg%3e");
  }

  :disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

export const DcCheckbox = React.forwardRef<HTMLInputElement, DcCheckboxProps>(
  (props, ref) => {
    const {
      type,
      value,
      disabled,
      checked,
      onChange,
      name,
      label,
      checkboxSize,
      ...rest
    } = props

    return (
      <StyledDcCheckbox checkboxSize={checkboxSize}>
        <StyledCheckBox
          type="checkbox"
          value={value}
          disabled={disabled}
          checked={checked}
          onChange={onChange}
          name={name}
          checkboxSize={checkboxSize}
          ref={ref}
          {...rest}
        />
        {label && (
          <StyledCheckBoxLabel checkboxSize={checkboxSize}>
            {label}
          </StyledCheckBoxLabel>
        )}
      </StyledDcCheckbox>
    )
  },
)

export default DcCheckbox
